import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Lock, LogOut } from 'lucide-react';

interface InactivityWarningProps {
  open: boolean;
  onStaySignedIn: () => void;
  onSignOut: () => void;
  countdownSeconds?: number;
}

export function InactivityWarning({ open, onStaySignedIn, onSignOut, countdownSeconds = 120 }: InactivityWarningProps) {
  const [remaining, setRemaining] = useState(countdownSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      clearCountdown();
      setRemaining(countdownSeconds);
      return;
    }

    setRemaining(countdownSeconds);
    intervalRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearCountdown();
          onSignOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return clearCountdown;
  }, [open, countdownSeconds, onSignOut, clearCountdown]);

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <AlertDialogTitle className="text-center">Session Expiring Soon</AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            Your session will expire in{' '}
            <span className="font-semibold text-foreground tabular-nums">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </span>
            {' '}due to inactivity. Any unsaved changes may be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onSignOut} className="gap-2">
            <LogOut className="w-4 h-4" /> Sign Out Now
          </Button>
          <Button onClick={onStaySignedIn} className="gap-2">
            Stay Signed In
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
